import {
  uploadExternalFile,
  deleteFile as deleteFileV2,
} from '@homeservices/api-kit/proreferral/files/v2';

import {
  uploadAsset, // Upload a file to core
} from '@homeservices/api-kit/asset';

import {
  createOccupationAsset, // Create an association to file in core
  deleteOccupationAsset, // Delete an association to file in core
} from '@homeservices/api-kit/serviceprovider/occupations';

import { VALID_IMAGE_TYPES, DEFAULT_MAX_IMAGE_SIZE } from '@/constants';

export async function _upload (body, fileName, config) {
  if (config.mode === 'provider') {
    // Upload the asset to coreassets in the 'service-providers-portfolio' namespace
    const uploadResponse = await uploadAsset('service-providers-portfolio', config.id, body);

    // Associate the asset to the service provider
    const createResponse = await createOccupationAsset(config.id, {
      asset_name: fileName,
      asset_path: uploadResponse.data.url,
      asset_thumbnail_path: uploadResponse.data.thumbnail,
      asset_type: 'picture',
      lifecycle_status: 'active',
    });

    // Set the created id that we actually care about
    uploadResponse.data.id = createResponse.data.id;

    return Promise.resolve(uploadResponse);
  }

  const { data: { url } } = await uploadAsset('work-orders-private-msg', '_upload', body);

  return uploadExternalFile({ external_file: url });
}

/**
 * Upload file(s)
 * @param {Object} event - The input event
 * @param {Object} config - The context of the file to delete.
 * @param {string} config.id - The id of the job or service provider. (Required if mode)
 * @param {string} config.mode - The way to delete the file. (provider | job | undefined)
 * @param {Number} config.size - max file size in MB to be accepted
 */
export function uploadFiles (event, config = {}) {
  const { id, mode, size = DEFAULT_MAX_IMAGE_SIZE } = config;
  if (mode && !['job', 'provider'].some(value => value === mode)) {
    throw Error(`uploadFiles invalid mode passed - ${mode}`);
  }

  if (mode && !id) {
    throw Error(`uploadFiles id not passed for mode - ${mode}`);
  }

  const promises = [];
  const files = event.target.files || event.dataTransfer.files;
  const validFiles = VALID_IMAGE_TYPES;
  if (files.length <= 8) {
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (validFiles.indexOf(file.type) < 0) {
        promises.push(Promise.reject(new Error(`${file.name} - photo must be a GIF, JPG, JPEG or PNG`)));
      } else if (file.size > size) {
        promises.push(Promise.reject(new Error(`${file.name} - photo size cannot exceed ${DEFAULT_MAX_IMAGE_SIZE / 1000000}MB`)));
      } else if (file.size <= 1) {
        promises.push(Promise.reject(new Error(`${file.name} - photo size cannot be 0 Bytes`)));
      } else {
        const formData = new FormData();
        formData.append('file', file);
        promises.push(_upload(formData, file.name, config));
      }
    }
    return promises;
  }
  return files;
}

/**
 * Delete file
 * @param {Object} config - The context of the file to delete.
 * @param {string} config.id - The id of the file/asset to delete (Required)
 * @param {string} config.mode - The way to delete the file. (provider | undefined)
 */
export function deleteFile (config = {}) {
  const { id, mode } = config;

  if (!id) {
    throw Error('deleteFile id not passed');
  }
  if (mode && mode !== 'provider') {
    throw Error(`deleteFile invalid mode passed - ${mode}`);
  }

  if (config.mode === 'provider') {
    return deleteOccupationAsset(id);
  }
  return deleteFileV2(id);
}
